import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Form,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from 'reactstrap';

import './ResetMfaModal.css';
import apiClient, { ApiError } from 'clients/ApiClient';
import { UserState } from 'store/redux/Authentication';

const ResetMfaModal = (props: Props) => {
  const { t } = useTranslation('translation');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const resetForm = () => {
    setPassword('');
    setMfaCode('');
    setIsLoading(false);
    setError('');
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!password || !mfaCode) return;
    setIsLoading(true);
    setError('');

    try {
      const authedClient = apiClient.withAccessToken(props.userState?.AccessToken ?? '');
      await authedClient.resetMfa({ password, mfaCode });
      props.onClose();
      props.logout?.call(undefined);
    } catch (error) {
      var apiError = error as ApiError;

      if (!apiError?.problem) {
        console.error(error);
        setError('modals.resetMfa.genericError');
        return;
      }

      if (apiError.status === 401) {
        props.logout?.call(undefined);
        props.onClose();
        return;
      }

      setError(`modals.resetMfa.problem.${apiError.problem.typeName}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Enter key for form submission
  const onKeyUp = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSubmit(e as unknown as FormEvent<HTMLFormElement>);
    }
  };

  return (
    <Modal isOpen={props.open} onClose={props.onClose} onClosed={resetForm}>
      <ModalHeader>{t('modals.resetMfa.header')}</ModalHeader>
      <ModalBody className="reset-mfa-body">
        <p>{t('modals.resetMfa.bodyInfo1')}</p>
        <p>{t('modals.resetMfa.bodyInfo2')}</p>
        <p>{t('modals.resetMfa.bodyInfo3')}</p>
        <p>{t('modals.resetMfa.bodySuccessInfo')}</p>
        <Form className="reset-mfa-form" onSubmit={onSubmit}>
          <Label>
            {t('login.LABEL_PASSWORD')}{' '}
            <Input
              placeholder={t('login.PROMPT_FOR_PASSWORD')}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyUp={onKeyUp}
              autoFocus
            />
          </Label>
          <Label>
            {t('modals.resetMfa.bodyMfaCode')}
            <Input
              placeholder={t('login.PROMPT_FOR_ONE_USE_CODE')}
              type="text"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              onKeyUp={onKeyUp}
            />
          </Label>
          <Button color="primary" type="submit" disabled={isLoading} className="reset-mfa-submit">
            {isLoading && <Spinner />}
            <span>{t('modals.resetMfa.buttonSubmit')}</span>
          </Button>
        </Form>
        {error && <Alert color="danger">{t(error)}</Alert>}
      </ModalBody>
      <ModalFooter>
        <Button onClick={props.onClose}>{t('modals.resetMfa.buttonClose')}</Button>
      </ModalFooter>
    </Modal>
  );
};

type Props = {
  userState?: UserState;
  open: boolean;
  onClose: () => void;
  logout?: () => void;
};

export default ResetMfaModal;
